import React, { useState, useEffect, useCallback, useRef } from 'react';
import { battleSocket } from '../../lib/battleSocket';
import logger from '../../lib/logger';
import { useGame } from '../../contexts/GameContext';
import Window from '../window-system/Window';
import BattleCanvas, { BattleCanvasHandle } from '../BattleCanvas';
import { AnimationCommand, AnimationEvent, QueueCompleteCallback } from '../../game/BattleAnimationManager';
import { getAvatarUrl } from '../../lib/avatars';
import './BattleWindow.css';
import EffectsCanvas, { EffectsCanvasHandle } from '../EffectsCanvas/EffectsCanvas';

interface BattleAction {
  type: 'attack' | 'ability';
  attackZone?: 'head' | 'body' | 'legs';
  blockZone?: 'head' | 'body' | 'legs';
  abilityId?: string;
}

interface AbilityInfo {
  id: string;
  name: string;
  description?: string;
  cost: number;
  cooldown: number;
  currentCooldown: number;
  available: boolean;
}

interface EffectInfo {
  type: string;
  name: string;
  duration: number;
  value?: number;
}

interface BattleState {
  battleId: string;
  round: number;
  yourCharacter: any;
  yourHP: number;
  yourMaxHP: number;
  yourResource: number;
  yourMaxResource: number;
  yourResourceType: 'none' | 'mana' | 'energy' | 'stamina';
  yourEffects: EffectInfo[];
  yourAbilities: AbilityInfo[];
  yourModel?: any;
  opponentCharacter: any;
  opponentHP: number;
  opponentMaxHP: number;
  opponentResource: number;
  opponentMaxResource: number;
  opponentResourceType: 'none' | 'mana' | 'energy' | 'stamina';
  opponentEffects: EffectInfo[];
  opponentModel?: any;
  environmentModel?: any;
  status: 'active' | 'finished';
}

interface RoundResult {
  battleId: string;
  round: number;
  firstPlayer: string;
  actions: Record<string, BattleAction>;
  events: any[];
  damage: Record<string, number>;
  newHP: Record<string, number>;
  newResource?: Record<string, number>;
  updatedEffects?: Record<string, EffectInfo[]>;
  updatedCooldowns?: Record<string, Record<string, number>>;
  battleEnded: boolean;
  winner?: string;
}

interface BattleWindowProps {
  onClose: () => void;
}

const BattleWindow: React.FC<BattleWindowProps> = ({ onClose }) => {
  const { state } = useGame();
  const { character } = state;
  const [battleState, setBattleState] = useState<BattleState | null>(null);
  const [selectedAttackZone, setSelectedAttackZone] = useState<'head' | 'body' | 'legs' | null>(null);
  const [selectedBlockZone, setSelectedBlockZone] = useState<'head' | 'body' | 'legs' | null>(null);
  const [selectedAbilityId, setSelectedAbilityId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'attack' | 'ability'>('attack');
  const [actionSubmitted, setActionSubmitted] = useState(false);
  const [opponentSubmitted, setOpponentSubmitted] = useState(false);
  const [battleWinner, setBattleWinner] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isBattleOver, setIsBattleOver] = useState(false);
  const battleWinnerRef = useRef(battleWinner);
  const isBattleOverRef = useRef(isBattleOver);
  const [currentRoundResult, setCurrentRoundResult] = useState<RoundResult | null>(null);
  const currentRoundResultRef = useRef<RoundResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [battleFinished, setBattleFinished] = useState(false);
  const [autoBattle, setAutoBattle] = useState(() => {
    try {
      const saved = localStorage.getItem('fight_autobattle_enabled');
      return saved ? JSON.parse(saved) : false;
    } catch (e) {
      return false;
    }
  });
  const battleCanvasRef = useRef<BattleCanvasHandle>(null);
  const effectsCanvasRef = useRef<EffectsCanvasHandle>(null);
  const autoBattleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [windowSize, setWindowSize] = useState(() => {
    try {
      const saved = localStorage.getItem('fight_window_battle-window_size');
      return saved ? JSON.parse(saved) : { width: 900, height: 600 };
    } catch (e) {
      return { width: 900, height: 600 };
    }
  });

  const [windowPosition, setWindowPosition] = useState(() => {
    try {
      const saved = localStorage.getItem('fight_window_battle-window_position');
      return saved ? JSON.parse(saved) : { x: window.innerWidth / 2 - 450, y: 50 };
    } catch (e) {
      return { x: window.innerWidth / 2 - 450, y: 50 };
    }
  });

  const handlePositionChange = (pos: { x: number; y: number }) => {
    setWindowPosition(pos);
    localStorage.setItem('fight_window_battle-window_position', JSON.stringify(pos));
  };

  const handleSizeChange = (size: { width: number; height: number }) => {
    setWindowSize(size);
    localStorage.setItem('fight_window_battle-window_size', JSON.stringify(size));
  };

  const handleAutoBattleChange = (enabled: boolean) => {
    setAutoBattle(enabled);
    localStorage.setItem('fight_autobattle_enabled', JSON.stringify(enabled));
  };

  useEffect(() => {
    battleWinnerRef.current = battleWinner;
  }, [battleWinner]);

  useEffect(() => {
    isBattleOverRef.current = isBattleOver;
  }, [isBattleOver]);

  useEffect(() => {
    if (!character) {
      onClose();
      return;
    }

    const connectToBattle = async () => {
      try {
        logger.info('BattleWindow', 'Connecting to battle namespace...');
        if (!battleSocket.isConnected()) {
          await battleSocket.connect();
        }
        logger.info('BattleWindow', 'Connected! Restoring battle state...');
        battleSocket.emit('battle.restore');
      } catch (error) {
        console.error('Failed to connect to battle:', error);
        setError('Не удалось подключиться к бою');
        setLoading(false);
      }
    };

    connectToBattle();

    const unsubRestored = battleSocket.on('battle.restored', (data: BattleState) => {
      setBattleState(data);
      setLoading(false);
      if (data.status === 'finished') {
        setBattleFinished(true);
      }
    });

    const unsubRoundResult = battleSocket.on('battle.round-result', (result: RoundResult) => {
      logger.info('[BattleWindow]', `Round ${result.round} result received. Processing animations.`);
      setIsProcessing(true);
      setCurrentRoundResult(result);
      currentRoundResultRef.current = result;

      const animations: AnimationCommand[] = result.events
        .flatMap((event: any) => {
          if (event.type === 'attack') {
            const attackerId = event.attacker === character?.id ? 'player' : 'opponent';
            const targetId = event.target === character?.id ? 'player' : 'opponent';
            
            return [
              {
                characterId: attackerId,
                action: 'attack',
                zone: event.zone,
                targetId: targetId,
              },
              {
                characterId: targetId,
                action: event.outcome.reaction,
                damage: event.outcome.damage,
                isCritical: event.outcome.isCritical,
              },
            ];
          } else if (event.type === 'ability') {
            const casterId = event.attacker === character?.id ? 'player' : 'opponent';
            const targetId = event.target === character?.id ? 'player' : 'opponent';
            
            const anims: AnimationCommand[] = [];
            
            anims.push({
              characterId: casterId,
              action: 'ability',
              targetId: targetId,
            });
            
            if (event.damage > 0) {
              const targetCharacterId = event.target;
              const targetHP = result.newHP[targetCharacterId];
              
              const reaction = targetHP <= 0 ? 'death' : 'hit';
              
              anims.push({
                characterId: targetId,
                action: reaction,
                damage: event.damage,
                isCritical: false,
              });
            }
            
            return anims;
          } else if (event.type === 'dot') {
            const targetId = event.target === character?.id ? 'player' : 'opponent';
            const targetCharacterId = event.target;
            const targetHP = result.newHP[targetCharacterId];
            
            const reaction = targetHP <= 0 ? 'death' : 'hit';
            
            return [{
              characterId: targetId,
              action: reaction,
              damage: event.damage,
              isCritical: false,
            }];
          }
          return [];
        });

      if (result.battleEnded) {
        logger.info('[BattleWindow]', 'This is the final round.');
        setIsBattleOver(true);
      }

      if (battleCanvasRef.current) {
        battleCanvasRef.current.playBattleAnimation(animations, result);
      }
    });

    const unsubFinished = battleSocket.on('battle.finished', (data: { winner: string }) => {
      logger.info('[BattleWindow]', 'Battle finished event received', data);
      setBattleWinner(data.winner);
    });

    return () => {
      unsubRestored();
      unsubRoundResult();
      unsubFinished();
      // Очищаем таймер автобоя если он есть
      if (autoBattleTimeoutRef.current) {
        clearTimeout(autoBattleTimeoutRef.current);
      }
    };
  }, [character, onClose, windowPosition, windowSize]);

  const handleQueueComplete = useCallback(() => {
    if (isBattleOverRef.current) {
      logger.info('[BattleWindow]', 'Final animation queue complete. Showing results.');
      if (effectsCanvasRef.current && battleWinnerRef.current && character) {
        const isWinner = battleWinnerRef.current === character.id;
        
        const centerX = windowSize.width / 2;
        const centerY = windowSize.height / 5;

        effectsCanvasRef.current.addEffect(
          isWinner ? 'Победа!' : 'Поражение',
          isWinner ? 'victory' : 'defeat',
          { x: centerX, y: centerY }
        );
      }
      setBattleFinished(true);
      setIsProcessing(false);
    } else {
      logger.info('[BattleWindow]', 'Animation queue complete. Updating battle state and unlocking UI.');
      
      setBattleState(prev => {
        const result = currentRoundResultRef.current;
        if (!prev || !result) {
          logger.warn('[BattleWindow]', 'No previous state or round result to update');
          return prev;
        }
        
        if (!result.newHP || !result.newHP[character!.id] || !result.newHP[prev.opponentCharacter.id]) {
          logger.error('[BattleWindow]', 'Missing HP data in round result', result);
          return prev;
        }
        
        const updatedAbilities = prev.yourAbilities.map(ability => {
          const newCooldown = result.updatedCooldowns?.[character!.id]?.[ability.id] || 0;
          const newResource = result.newResource?.[character!.id] ?? prev.yourResource;
          const hasResource = ability.cost === 0 || newResource >= ability.cost;
          
          return {
            ...ability,
            currentCooldown: newCooldown,
            available: hasResource && newCooldown === 0
          };
        });
        
        const oldResource = prev.yourResource;
        const newResource = result.newResource?.[character!.id] ?? prev.yourResource;
        
        logger.info('[BattleWindow]', 'Updating battle state:', {
          round: result.round + 1,
          yourHP: result.newHP[character!.id],
          opponentHP: result.newHP[prev.opponentCharacter.id],
          yourResource: newResource,
          opponentResource: result.newResource?.[prev.opponentCharacter.id],
          resourceChange: newResource - oldResource,
          resourceType: prev.yourResourceType
        });
        
        return {
          ...prev,
          round: result.round + 1,
          yourHP: result.newHP[character!.id],
          opponentHP: result.newHP[prev.opponentCharacter.id],
          yourResource: result.newResource?.[character!.id] ?? prev.yourResource,
          opponentResource: result.newResource?.[prev.opponentCharacter.id] ?? prev.opponentResource,
          yourEffects: result.updatedEffects?.[character!.id] ?? prev.yourEffects,
          opponentEffects: result.updatedEffects?.[prev.opponentCharacter.id] ?? prev.opponentEffects,
          yourAbilities: updatedAbilities
        };
      });
      
      setActionSubmitted(false);
      setOpponentSubmitted(false);
      setSelectedAttackZone(null);
      setSelectedBlockZone(null);
      setSelectedAbilityId(null);
      setActionType('attack');
      setIsProcessing(false);
    }
  }, [character, windowSize, currentRoundResult]);

  useEffect(() => {
    return () => {
      // Не отключаем battleSocket полностью, только очищаем таймер автобоя
      if (autoBattleTimeoutRef.current) {
        clearTimeout(autoBattleTimeoutRef.current);
      }
    }
  }, []);

  const handleAnimationEvent = useCallback((event: AnimationEvent) => {
    logger.info('[BattleWindow]', 'Handling animation event:', event);
    if (!battleCanvasRef.current || !effectsCanvasRef.current || !character || !event.roundResult) {
      logger.warn('[BattleWindow]', 'Cannot handle animation event, refs, character or roundResult missing.');
      return;
    }
    
    const position = battleCanvasRef.current.getCharacterPosition(event.targetId);
    if (!position) {
      logger.warn('[BattleWindow]', `Could not get position for target ${event.targetId}. Cannot show effect.`);
      return;
    }

    let effectText: string = '';
    let effectType: 'damage' | 'crit' | 'block' | 'dodge' | 'reduced' = 'damage';

    switch (event.type) {
      case 'hit':
      case 'death':
        effectText = event.damage?.toString() || '0';
        // Проверяем, есть ли у цели щиты или эффекты снижения урона
        const isPlayerTarget = event.targetId === 'player';
        const targetEffects = isPlayerTarget ? battleState?.yourEffects : battleState?.opponentEffects;
        const hasShield = targetEffects?.some(e => e.type === 'shield' && e.duration > 0);
        const hasDamageReduction = targetEffects?.some(e => e.type === 'reduce_damage' && e.duration > 0);
        
        // Если есть щиты или снижение урона - показываем желтым
        if (hasShield || hasDamageReduction) {
          effectType = 'reduced';
        } else {
          effectType = event.isCritical ? 'crit' : 'damage';
        }
        break;
      case 'block':
        if (event.damage && event.damage > 0) {
          // Если блок был пробит и прошел урон - показываем урон желтым цветом
          effectText = event.damage.toString();
          effectType = 'reduced';
        } else {
          // Если блок полностью поглотил урон - показываем "Блок!"
          effectText = 'Блок!';
          effectType = 'block';
        }
        break;
      case 'dodge':
        effectText = 'Уворот!';
        effectType = 'dodge';
        break;
      case 'ability':
        if (event.damage && event.damage > 0) {
          effectText = event.damage.toString();
          // Проверяем, есть ли у цели щиты или эффекты снижения урона
          const isPlayerTarget = event.targetId === 'player';
          const targetEffects = isPlayerTarget ? battleState?.yourEffects : battleState?.opponentEffects;
          const hasShield = targetEffects?.some(e => e.type === 'shield' && e.duration > 0);
          const hasDamageReduction = targetEffects?.some(e => e.type === 'reduce_damage' && e.duration > 0);
          
          // Если есть щиты или снижение урона - показываем желтым
          if (hasShield || hasDamageReduction) {
            effectType = 'reduced';
          } else {
            effectType = event.isCritical ? 'crit' : 'damage';
          }
        }
        break;
    }
    
    if (effectText) {
      logger.info('[BattleWindow]', `Adding effect: "${effectText}" at`, position);
      effectsCanvasRef.current.addEffect(effectText, effectType, position);
    }

    if ((event.type === 'hit' || event.type === 'death' || event.type === 'ability' || event.type === 'block') && event.damage && event.damage > 0) {
      setBattleState(prev => {
        if (!prev || !event.roundResult) return prev;
        
        const isPlayerTarget = event.targetId === 'player';
        const targetCharacterId = isPlayerTarget ? character.id : prev.opponentCharacter.id;
        
        const newHP = event.roundResult.newHP[targetCharacterId];
        
        if (newHP === undefined) return prev;
        
        logger.info('[BattleWindow]', `Instantly updating HP for ${event.targetId}: ${newHP} (${event.type})`);
        
        if (isPlayerTarget) {
          return { ...prev, yourHP: newHP };
        } else {
          return { ...prev, opponentHP: newHP };
        }
      });
    }
  }, [character]);

  const submitAction = useCallback(() => {
    if (!battleSocket.isConnected() || !battleState || actionSubmitted) return;
    
    let action: BattleAction;
    
    if (selectedAbilityId) {
      action = {
        type: 'ability',
        abilityId: selectedAbilityId
      };
      
      const ability = battleState.yourAbilities.find(a => a.id === selectedAbilityId);
      if (ability) {
        console.log('[BattleWindow] Using ability:', {
          abilityId: selectedAbilityId,
          abilityName: ability.name,
          cost: ability.cost,
          currentResource: battleState.yourResource,
          maxResource: battleState.yourMaxResource,
          resourceType: battleState.yourResourceType
        });
      }
    } else {
      if (!selectedAttackZone || !selectedBlockZone) {
        setError('Выберите зону атаки и блока');
        return;
      }
      action = {
        type: 'attack',
        attackZone: selectedAttackZone,
        blockZone: selectedBlockZone
      };
    }

    battleSocket.emit('battle.submit-action', {
      battleId: battleState.battleId,
      action: action
    });

    setActionSubmitted(true);
    setSelectedAbilityId(null);
    setActionType('attack');
  }, [battleState, selectedAttackZone, selectedBlockZone, selectedAbilityId, actionSubmitted]);

  useEffect(() => {
    const canMakeMove = !isProcessing && !actionSubmitted && !battleFinished;

    if (autoBattle && canMakeMove) {
      const delay = Math.random() * 2000 + 5000; // 5-7 секунд
      autoBattleTimeoutRef.current = setTimeout(() => {
        if (!battleSocket.isConnected() || !battleState) return;

        // Проверяем доступные абилки
        const availableAbilities = battleState.yourAbilities?.filter(ability => ability.available) || [];
        
        let action: BattleAction;
        
        // Если есть доступные абилки, используем их с вероятностью 70%
        if (availableAbilities.length > 0 && Math.random() < 0.7) {
          // Выбираем случайную доступную абилку
          const randomAbility = availableAbilities[Math.floor(Math.random() * availableAbilities.length)];
          
          action = {
            type: 'ability',
            abilityId: randomAbility.id
          };
          
          console.log('[AutoBattle] Using ability:', randomAbility.name);
        } else {
          // Иначе делаем обычную атаку
          const zones: Array<'head' | 'body' | 'legs'> = ['head', 'body', 'legs'];
          const randomAttackZone = zones[Math.floor(Math.random() * zones.length)];
          const randomBlockZone = zones[Math.floor(Math.random() * zones.length)];
          
          action = {
            type: 'attack',
            attackZone: randomAttackZone,
            blockZone: randomBlockZone
          };
          
          setSelectedAttackZone(randomAttackZone);
          setSelectedBlockZone(randomBlockZone);
          
          console.log('[AutoBattle] Using attack:', randomAttackZone, 'block:', randomBlockZone);
        }
        
        battleSocket.emit('battle.submit-action', {
          battleId: battleState.battleId,
          action: action
        });
        setActionSubmitted(true);
      }, delay);
    }

    return () => {
      if (autoBattleTimeoutRef.current) {
        clearTimeout(autoBattleTimeoutRef.current);
      }
    };
  }, [autoBattle, isProcessing, actionSubmitted, battleFinished, battleState]);

  const selectAttackZone = (zone: 'head' | 'body' | 'legs') => {
    if (actionSubmitted || isProcessing || battleFinished) return;
    setSelectedAttackZone(zone);
    setSelectedAbilityId(null);
    setActionType('attack');
    setError(null);
  };

  const selectBlockZone = (zone: 'head' | 'body' | 'legs') => {
    if (actionSubmitted || isProcessing || battleFinished) return;
    setSelectedBlockZone(zone);
    setSelectedAbilityId(null);
    setActionType('attack');
    setError(null);
  };

  const selectAbility = (abilityId: string) => {
    if (actionSubmitted || isProcessing || battleFinished) return;
    setSelectedAbilityId(abilityId);
    setSelectedAttackZone(null);
    setSelectedBlockZone(null);
    setActionType('ability');
    setError(null);
  };

  const getModelData = (model: any) => {
    if (!model || !model.metadata) {
      return {
        modelUrl: '',
        textures: {},
        animationMapping: {},
        baseUrl: '',
        scale: 1,
        eventFrames: {}
      };
    }

    const baseModelFile = model.metadata.files?.find((f: any) => 
      f.name === model.metadata.modelMapping?.baseModel
    );
    
    const textureFiles = model.metadata.files?.filter((f: any) => 
      f.name.endsWith('.png')
    ) || [];
    
    const textures: Record<string, string> = {};
    textureFiles.forEach((file: any) => {
      textures[file.name] = file.url;
    });

    return {
      modelUrl: baseModelFile?.url || '',
      textures,
      animationMapping: model.metadata.animationMapping || {},
      baseUrl: model.file_path || '',
      scale: model.metadata.modelMapping?.scale || 1,
      eventFrames: model.metadata.eventFrames || {}
    };
  };

  const getEnvironmentModelData = (model: any) => {
    if (!model || !model.metadata) {
      return undefined;
    }

    const baseModelFile = model.metadata.files?.find((f: any) => 
      f.name === model.metadata.modelMapping?.baseModel
    );
    
    if (!baseModelFile) {
      return undefined;
    }

    const textureFiles = model.metadata.files?.filter((f: any) => 
      f.name.endsWith('.png') || f.name.endsWith('.jpg') || f.name.endsWith('.jpeg')
    ) || [];
    
    const textureUrls = textureFiles.map((file: any) => file.url);

    const environmentData = {
      modelUrl: baseModelFile.url,
      textures: textureUrls,
      scale: model.metadata.modelMapping?.scale || 1,
      position: model.metadata.modelMapping?.position || { x: 0, y: 0, z: 0 }
    };

    console.log('[BattleWindow] Environment model data:', {
      modelName: model.name,
      scale: environmentData.scale,
      position: environmentData.position,
      metadata: model.metadata
    });

    return environmentData;
  };

  if (!battleState) {
    return null;
  }

  const playerModelData = getModelData(battleState.yourModel);
  const opponentModelData = getModelData(battleState.opponentModel);
  const environmentModelData = getEnvironmentModelData(battleState.environmentModel);

  return (
    <Window
      id="battle-window"
      title={`Бой - Раунд ${battleState.round}`}
      position={windowPosition}
      size={windowSize}
      onClose={onClose}
      onFocus={() => {}}
      onPositionChange={handlePositionChange}
      onSizeChange={handleSizeChange}
      minWidth={800}
      minHeight={500}
      zIndex={2000}
      closable={battleFinished}
      lockAspectRatio={true}
      contentClassName="battle-window-content"
    >
      <div className="battle-window-layout">
        <EffectsCanvas ref={effectsCanvasRef} />
        <div className="battle-canvas-container">
          <BattleCanvas
            ref={battleCanvasRef}
            width={windowSize.width}
            height={windowSize.height}
            playerModel={playerModelData}
            opponentModel={opponentModelData}
            environmentModel={environmentModelData}
            onAnimationEvent={handleAnimationEvent}
            onQueueComplete={handleQueueComplete}
          />
        </div>

        {/* Abilities Panel */}
        {battleState.yourAbilities && battleState.yourAbilities.length > 0 && (
          <div className="abilities-panel">
            {battleState.yourAbilities.map((ability) => (
              <div 
                key={ability.id}
                className={`ability-button ${selectedAbilityId === ability.id ? 'selected' : ''} ${!ability.available ? 'disabled' : ''}`}
                onClick={() => ability.available && selectAbility(ability.id)}
                title={`${ability.name}${ability.description ? '\n' + ability.description : ''}\nСтоимость: ${ability.cost}\nКулдаун: ${ability.cooldown}`}
              >
                <div className="ability-icon">
                  <span className="ability-letter">{ability.name.charAt(0)}</span>
                </div>
                {ability.cost > 0 && (
                  <div className="ability-cost">{ability.cost}</div>
                )}
                {ability.currentCooldown > 0 && (
                  <div className="ability-cooldown-overlay">
                    <span className="cooldown-number">{ability.currentCooldown}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Player Info Overlay */}
        <div className="player-info-overlay player">
          <img src={getAvatarUrl(battleState.yourCharacter.avatar)} alt={battleState.yourCharacter.nickname} className="player-avatar" />
          <div className="player-details">
            <h3 className="player-name">{battleState.yourCharacter.nickname}</h3>
            <div className="character-hp">
              <div className="hp-bar">
                <div className="hp-fill" style={{ width: `${(battleState.yourHP / battleState.yourMaxHP) * 100}%` }} />
              </div>
              <span className="hp-text">{battleState.yourHP} / {battleState.yourMaxHP}</span>
            </div>
            {battleState.yourResourceType !== 'none' && (
              <div className="character-resource">
                <div className="resource-bar">
                  <div 
                    className={`resource-fill ${battleState.yourResourceType}`} 
                    style={{ width: `${(battleState.yourResource / battleState.yourMaxResource) * 100}%` }} 
                  />
                </div>
                <span className="resource-text">
                  {battleState.yourResource} / {battleState.yourMaxResource}
                </span>
              </div>
            )}
            {battleState.yourEffects.length > 0 && (
              <div className="character-effects">
                {battleState.yourEffects.map((effect, index) => (
                  <div key={index} className={`effect-icon ${effect.type}`} title={`${effect.name} (${effect.duration})`}>
                    <span className="effect-duration">{effect.duration}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Opponent Info Overlay */}
        <div className="player-info-overlay opponent">
          <img src={getAvatarUrl(battleState.opponentCharacter.avatar)} alt={battleState.opponentCharacter.nickname} className="player-avatar" />
          <div className="player-details">
            <h3 className="player-name">{battleState.opponentCharacter.nickname}</h3>
            <div className="character-hp">
              <div className="hp-bar">
                <div className="hp-fill opponent" style={{ width: `${(battleState.opponentHP / battleState.opponentMaxHP) * 100}%` }} />
              </div>
              <span className="hp-text">{battleState.opponentHP} / {battleState.opponentMaxHP}</span>
            </div>
            {battleState.opponentResourceType !== 'none' && (
              <div className="character-resource">
                <div className="resource-bar">
                  <div 
                    className={`resource-fill ${battleState.opponentResourceType}`} 
                    style={{ width: `${(battleState.opponentResource / battleState.opponentMaxResource) * 100}%` }} 
                  />
                </div>
                <span className="resource-text">
                  {battleState.opponentResource} / {battleState.opponentMaxResource}
                </span>
              </div>
            )}
            {battleState.opponentEffects.length > 0 && (
              <div className="character-effects">
                {battleState.opponentEffects.map((effect, index) => (
                  <div key={index} className={`effect-icon ${effect.type}`} title={`${effect.name} (${effect.duration})`}>
                    <span className="effect-duration">{effect.duration}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Controls Overlay */}
        <div className="battle-controls-overlay">
          {!battleFinished && !isProcessing && !actionSubmitted && (
            <div className="controls-container">
              <div className="action-buttons-group">
                <button className={`action-button block ${selectedBlockZone === 'head' ? 'selected' : ''}`} onClick={() => selectBlockZone('head')} disabled={actionSubmitted}>
                  <span className="icon">🛡️</span> Голова
                </button>
                <button className={`action-button block ${selectedBlockZone === 'body' ? 'selected' : ''}`} onClick={() => selectBlockZone('body')} disabled={actionSubmitted}>
                  <span className="icon">🛡️</span> Тело
                </button>
                <button className={`action-button block ${selectedBlockZone === 'legs' ? 'selected' : ''}`} onClick={() => selectBlockZone('legs')} disabled={actionSubmitted}>
                  <span className="icon">🛡️</span> Ноги
                </button>
              </div>
              <button className="submit-action-button" onClick={submitAction} disabled={actionSubmitted || (!selectedAbilityId && (!selectedAttackZone || !selectedBlockZone))}>
                {actionSubmitted ? 'Ожидание...' : 'В бой!'}
              </button>
              <div className="action-buttons-group">
                <button className={`action-button attack ${selectedAttackZone === 'head' ? 'selected' : ''}`} onClick={() => selectAttackZone('head')} disabled={actionSubmitted}>
                  <span className="icon attack-head">🗡️</span> Голова
                </button>
                <button className={`action-button attack ${selectedAttackZone === 'body' ? 'selected' : ''}`} onClick={() => selectAttackZone('body')} disabled={actionSubmitted}>
                  <span className="icon attack-body">🗡️</span> Тело
                </button>
                <button className={`action-button attack ${selectedAttackZone === 'legs' ? 'selected' : ''}`} onClick={() => selectAttackZone('legs')} disabled={actionSubmitted}>
                  <span className="icon attack-legs">🗡️</span> Ноги
                </button>
              </div>
            </div>
          )}
          
          {/* Auto Battle Toggle */}
          <div className="auto-battle-container">
            <label className="auto-battle-toggle">
              <input
                type="checkbox"
                checked={autoBattle}
                onChange={(e) => handleAutoBattleChange(e.target.checked)}
              />
              <span className="toggle-label">Авто-бой</span>
            </label>
          </div>

          {/* Status Messages */}
          {isProcessing && (
            <div className="battle-status processing">
              Обработка действий...
            </div>
          )}
          {actionSubmitted && !isProcessing && (
            <div className="battle-status waiting">
              Ожидание противника...
            </div>
          )}
          {error && (
            <div className="battle-status error">
              {error}
            </div>
          )}
        </div>
      </div>
    </Window>
  );
};

export default BattleWindow;
